import {
  CompRef,
  DocumentServicesObject,
  PopulatedComponentContainerInfoMap,
} from '@wix/document-services-types';
import {
  WidgetPointer,
  WidgetSlot,
  WidgetSlotPosition,
  SlotDefinition,
  UniversalSlotPointer,
} from '@wix/editor-platform-host-integration-apis';
import { PluginHostApiImplementation } from '../types';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import {
  createWidgetPluginComponentDefinition,
  filterPublicSlots,
  getPluginInfoByCompRef,
  getSlotDefinitions,
} from '../utils';
import { SlotPointer } from '../slotPointer';
import { getAppDataByCompRef } from '../utils/getAppData';

function getSlotContent(
  infoMap: PopulatedComponentContainerInfoMap,
  slotKeyName: string,
) {
  return Object.entries(infoMap).find(
    ([_key, value]) => value?.slotName === slotKeyName,
  )?.[1];
}

function getDmSlotsData(
  documentServicesAPI: DocumentServicesObject,
  hostCompRef: CompRef,
  innerElementPath?: string[],
): PopulatedComponentContainerInfoMap {
  if (innerElementPath && innerElementPath?.length > 0) {
    return documentServicesAPI.components.innerElements.slots.getPopulatedComponentContainerInfoMap(
      hostCompRef,
      innerElementPath,
    );
  }

  return documentServicesAPI.components.slots.builder.getPopulatedComponentContainerInfoMap(
    hostCompRef,
  );
}

interface PopulatedSlot {
  populatedCompRef: CompRef | undefined;
  slotDefinition: SlotDefinition;
}

function getSlotsContent(
  documentServicesAPI: DocumentServicesObject,
  hostCompRef: CompRef,
  slotDefinitions: SlotDefinition[],
): PopulatedSlot[] {
  return slotDefinitions.map((slotDefinition) => {
    const slotsData = getDmSlotsData(
      documentServicesAPI,
      hostCompRef,
      slotDefinition.innerElementPath,
    );

    const populatedSlotData = getSlotContent(slotsData, slotDefinition.name);

    return { populatedCompRef: populatedSlotData?.compRef, slotDefinition };
  });
}

function addWidgetSlotData(
  documentServicesAPI: DocumentServicesObject,
  hostCompRef: CompRef,
  slotDefinitions: SlotDefinition[],
): WidgetSlot[] {
  return getSlotsContent(documentServicesAPI, hostCompRef, slotDefinitions).map(
    ({ populatedCompRef, slotDefinition }) => {
      const pluginInfo = populatedCompRef
        ? getPluginInfoByCompRef(documentServicesAPI, populatedCompRef)
        : undefined;
      const result = {
        compRef: {
          id: 'not_supported_for_builder',
          type: 'DESKTOP',
        } as CompRef,
        pluginInfo,
        hostCompRef,
        id: slotDefinition.id,
        role: slotDefinition.name,
        placement: {
          appDefinitionId: slotDefinition.appDefinitionId,
          widgetId: slotDefinition.widgetId,
          slotId: slotDefinition.name,
        },
      };
      return result;
    },
  );
}

export function createBuilderHostAPI(
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
): PluginHostApiImplementation {
  return {
    getSlotPlaceholderDisplayName(_compRef: CompRef): string | undefined {
      throw new Error('Not implemented');
    },

    getWidgetHostRefBySlotContainer(slotPointer: SlotPointer): CompRef {
      return slotPointer.toBuilder().hostRef;
    },
    getWidgetSlot(
      slotPointer: SlotPointer,
      { includePrivate }: { includePrivate: boolean },
    ): WidgetSlot {
      const hostRef = this.getWidgetHostRefBySlotContainer(slotPointer);
      const allSlots = this.getWidgetSlots(hostRef, { includePrivate });
      const slot = allSlots.find(
        (sl) => sl.id === slotPointer.toBuilder().slotId,
      );
      if (!slot) {
        throw new Error(`Slot not found for ${slotPointer.toBuilder().slotId}`);
      }
      return slot;
    },

    getWidgetSlots(
      widgetCompRef: CompRef,
      options: { includePrivate: boolean },
    ): WidgetSlot[] {
      const slotDefinitions = getSlotDefinitions(
        documentServicesAPI,
        widgetCompRef,
        experimentsAPI,
      );

      const filteredSlotDefinitions = options.includePrivate
        ? slotDefinitions
        : filterPublicSlots(slotDefinitions);

      return addWidgetSlotData(
        documentServicesAPI,
        widgetCompRef,
        filteredSlotDefinitions,
      );
    },

    getWidgetHostRef(_compRef: CompRef): CompRef {
      throw new Error('Not implemented');
    },

    async populateWidgetPluginToSlot(
      slotPointer: SlotPointer,
      widgetPointer: WidgetPointer,
    ): Promise<CompRef> {
      const path = slotPointer.toBuilder();
      const appData = documentServicesAPI.tpa.app.getDataByAppDefId(
        widgetPointer.appDefinitionId,
      );

      const widgetComponent = await createWidgetPluginComponentDefinition(
        documentServicesAPI,
        widgetPointer,
        appData.applicationId!,
        experimentsAPI,
      );

      if (typeof path.innerElementPath === 'undefined') {
        return documentServicesAPI.components.slots.populate(
          path.hostRef,
          path.slotKeyName,
          widgetComponent,
        );
      }

      return documentServicesAPI.components.innerElements.slots.populate(
        path.hostRef,
        path.innerElementPath,
        path.slotKeyName,
        widgetComponent,
      ) as CompRef;
    },

    uninstallWidgetPlugin(slotPointer: SlotPointer): void {
      const path = slotPointer.toBuilder();

      if (typeof path.innerElementPath === 'undefined') {
        return documentServicesAPI.components.slots.remove(
          path.hostRef,
          path.slotKeyName,
        );
      }

      return documentServicesAPI.components.innerElements.slots.remove(
        path.hostRef,
        path.innerElementPath,
        path.slotKeyName,
      );
    },
    getWidgetPointer(slotPointer: SlotPointer): WidgetPointer | undefined {
      const path = slotPointer.toBuilder();
      const slotsData = getDmSlotsData(
        documentServicesAPI,
        path.hostRef,
        path.innerElementPath,
      );

      const populatedSlotData = getSlotContent(slotsData, path.slotKeyName);

      const populatedCompRef = populatedSlotData?.compRef;

      if (!populatedCompRef) {
        return undefined;
      }

      const { appDefinitionId, widgetId } = getAppDataByCompRef(
        documentServicesAPI,
        populatedCompRef,
      );

      return { appDefinitionId, widgetId };
    },

    getUniversalSlotPointerByPluginCompRef(
      widgetPluginCompRef: CompRef,
    ): UniversalSlotPointer | undefined {
      if (
        !documentServicesAPI.components.slots.isChildOfSlottedComp(
          widgetPluginCompRef,
        )
      ) {
        return undefined;
      }
      const hostRef =
        documentServicesAPI.components.slots.builder.getSlottedComponentOwner(
          widgetPluginCompRef,
        )?.compPointer as CompRef;

      if (!hostRef) {
        return undefined;
      }

      const slotDefinitions = getSlotDefinitions(
        documentServicesAPI,
        hostRef,
        experimentsAPI,
      );

      const matchingSlot = getSlotsContent(
        documentServicesAPI,
        hostRef,
        slotDefinitions,
      ).find(
        ({ populatedCompRef }) =>
          populatedCompRef?.id === widgetPluginCompRef.id,
      );
      if (!matchingSlot) {
        return undefined;
      }
      return {
        hostCompRef: hostRef,
        slotId: matchingSlot.slotDefinition.id,
      };
    },
    /**
     * @deprecated Will be deprecated in upcoming PRs — use
     * `getUniversalSlotPointerByPluginCompRef` instead.
     */
    getSlotPlaceholderRefByContentRef(
      widgetPluginCompRef: CompRef,
    ): UniversalSlotPointer | CompRef | undefined {
      return this.getUniversalSlotPointerByPluginCompRef(widgetPluginCompRef);
    },

    isSlotPublic(_slotPointer: SlotPointer): boolean {
      throw new Error('Not implemented');
    },

    getSlotPosition(_compRef: CompRef): WidgetSlotPosition | null {
      throw new Error('Not implemented');
    },

    getSlotContentRef(_slotPointer: SlotPointer): CompRef | undefined {
      throw new Error('Not implemented');
    },

    getCompRefForSlotSelection(_slotPointer: SlotPointer): CompRef | undefined {
      throw new Error('Not implemented');
    },

    hasWidgetSlot(_widgetCompRef: CompRef): boolean {
      throw new Error('Not implemented');
    },

    async highlightSlot(_slotPointer: SlotPointer): Promise<void> {
      throw new Error('Not implemented');
    },

    unhighlightSlot(_slotPointer: SlotPointer): void {
      throw new Error('Not implemented');
    },

    isPluginComponent(compRef: CompRef): boolean {
      return !!this.getUniversalSlotPointerByPluginCompRef(compRef);
    },

    isWidgetSlotPopulated(_slotPointer: SlotPointer): boolean {
      throw new Error('Not implemented');
    },
  };
}
